/**
 * 数智大脑 Store — 主入口
 *
 * 将原 1102 行巨型 store 拆分为 5 个职责子模块，通过此入口组合并统一导出。
 * 外部导入路径保持不变（`@/stores/commandCenter`）。
 *
 * 子模块：
 * - conversation: 会话管理（CRUD、草稿、运行态、Agent 标签）
 * - dashboard: 数据面板（KPI、推送、记忆、任务、早报）
 * - todo: 待办事项
 * - proposal: 提案审批
 * - autonomy: 自主模式
 */
// TODO: [useApiQuery 迁移] fetchSessions 等多个方法的 loading/try/catch/finally 模板代码可用 useApiQuery 声明式替换
import { defineStore } from 'pinia'
import { useConversationSlice } from './conversation'
import { useDashboardSlice } from './dashboard'
import { useTodoSlice } from './todo'
import { useProposalSlice } from './proposal'
import { useAutonomySlice } from './autonomy'

// 类型 re-export，保持外部导入路径兼容
export type {
  HistoryConversation,
  ConversationRuntimeStatus,
  ConversationDraft,
  AutonomyConfig,
  AutonomyTaskStats,
  AutonomyEvent,
} from './types'

export const useCommandCenterStore = defineStore('commandCenter', () => {
  const conversation = useConversationSlice()
  const dashboard = useDashboardSlice()
  const todo = useTodoSlice()
  const proposal = useProposalSlice()
  const autonomy = useAutonomySlice()

  return {
    // ===== Dashboard（数据面板） =====
    query: dashboard.query,
    kpis: dashboard.kpis,
    feeds: dashboard.feeds,
    memories: dashboard.memories,
    executingTasks: dashboard.executingTasks,
    loading: dashboard.loading,
    currentUser: dashboard.currentUser,
    alertCount: dashboard.alertCount,
    briefingSummary: dashboard.briefingSummary,
    briefingItems: dashboard.briefingItems,
    hasActiveTasks: dashboard.hasActiveTasks,
    setQuery: dashboard.setQuery,
    submitQuery: dashboard.submitQuery,
    addKPI: dashboard.addKPI,
    removeKPI: dashboard.removeKPI,
    addFeed: dashboard.addFeed,
    removeFeed: dashboard.removeFeed,
    addMemory: dashboard.addMemory,
    addTask: dashboard.addTask,
    updateTaskProgress: dashboard.updateTaskProgress,

    // ===== Conversation（会话管理） =====
    historyConversations: conversation.historyConversations,
    activeConversationId: conversation.activeConversationId,
    sessionsLoading: conversation.sessionsLoading,
    sessionsError: conversation.sessionsError,
    sessionsPagination: conversation.sessionsPagination,
    currentProjectId: conversation.currentProjectId,
    currentUserId: conversation.currentUserId,
    updateProjectId: conversation.updateProjectId,
    updateUserId: conversation.updateUserId,
    upsertConversationDraft: conversation.upsertConversationDraft,
    setConversationRuntimeState: conversation.setConversationRuntimeState,
    addHistoryConversation: conversation.addHistoryConversation,
    setActiveConversation: conversation.setActiveConversation,
    loadConversation: conversation.loadConversation,
    fetchSessions: conversation.fetchSessions,
    loadMoreSessions: conversation.loadMoreSessions,
    refreshSessions: conversation.refreshSessions,
    createSession: conversation.createSession,
    createSessionInBackground: conversation.createSessionInBackground,
    replaceConversationSessionId: conversation.replaceConversationSessionId,
    updateSessionTitle: conversation.updateSessionTitle,
    updateConversationAgents: conversation.updateConversationAgents,
    setConversationAgents: conversation.setConversationAgents,
    deleteSession: conversation.deleteSession,

    // ===== Todo（待办事项） =====
    todoItems: todo.todoItems,
    todoBadgeAnimating: todo.todoBadgeAnimating,
    unfinishedTodoCount: todo.unfinishedTodoCount,
    addTodoItem: todo.addTodoItem,
    addTodoFromFeed: todo.addTodoFromFeed,
    toggleTodoItem: todo.toggleTodoItem,
    removeTodoItem: todo.removeTodoItem,
    getTodoByFeedId: todo.getTodoByFeedId,
    triggerTodoBadgeAnimation: todo.triggerTodoBadgeAnimation,

    // ===== Proposal（提案审批） =====
    proposalLoading: proposal.proposalLoading,
    proposalError: proposal.proposalError,
    approveProposal: proposal.approveProposal,
    denyProposal: proposal.denyProposal,

    // ===== Autonomy（自主模式） =====
    autonomyConfig: autonomy.autonomyConfig,
    isAutonomyEnabled: autonomy.isAutonomyEnabled,
    autonomyTaskStats: autonomy.autonomyTaskStats,
    autonomyRecentEvents: autonomy.autonomyRecentEvents,
    fetchAutonomyConfig: autonomy.fetchAutonomyConfig,
    updateAutonomyTaskStats: autonomy.updateAutonomyTaskStats,
    addAutonomyEvent: autonomy.addAutonomyEvent,
  }
})
